import { Component, inject, input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AddResumeDocument } from 'src/app/models/resume/add-resume';
import { ResumeService } from 'src/app/services/resume.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';

type Document = {
  resumeId?: number | null;
  resumeRequiredDocumentId: number;
  documentName: string;
  documentDescription: string;
  resumeDocumentId: number | null;
  file: string | null;
  fileId?: number;
  fileName: string | null;
  createDate: string | null;
  isEnabled?: boolean;
  temporalFile?: File;
}

@Component({
  selector: 'app-cv-documents',
  templateUrl: './cv-documents.component.html',
  styleUrl: './cv-documents.component.css'
})
export class CvDocumentsComponent implements OnChanges {
  public resumeId = input<number | null>(null);

  private readonly resumeService = inject(ResumeService);
  private readonly _toastrService = inject(ToastrService);

  public documents = signal<Document[]>([]);

  loading =  signal<{
    isLoading: boolean,
    id: number
  }>({
    isLoading: false,
    id: 0
  });

  ngOnInit(): void {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resumeId'].currentValue) {
      this.getDocuments();
    } else {
      this.getDocuments();
    }
  }

  getDocuments() {
    this.resumeService.getResumeDocument(this.resumeId()).subscribe({
      next: res => {
        this.documents.set(res.map(d => ({
          resumeId: null,
          resumeRequiredDocumentId: d.resumeRequiredDocumentId,
          documentName: d.documentName,
          documentDescription: d.documentDescription,
          resumeDocumentId: d.resumeDocumentId,
          file: null,
          fileName: null,
          fileId: d.fileId ?? 0,
          createDate: d.createDate
        })));
      }
    });
  }

  onFileSelected(event: any, resumeRequiredDocumentId: number) {
    const maxFileSize = 10485760; // 10 MB in bytes
    const file = event.target.files[0];
 
    if (file && file.size > maxFileSize) {
      const maxFileSizeMB = maxFileSize / (1024 * 1024);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      this._toastrService.warning(
        `El tamaño del archivo seleccionado (${fileSizeMB} MB) excede el límite permitido de ${maxFileSizeMB} MB.`
      );
      event.target.value = null;
      return;
    }

    if (file) {
      if (file.type !== 'application/pdf') {
        this._toastrService.error('Solo se permiten archivos PDF.');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result?.toString().split(',')[1] || null;
        if (!base64String) {
          return;
        }

        if (this.resumeId()) {
          this.addDocument(base64String, file.name, resumeRequiredDocumentId);
        } else {
          this.documents.set(this.documents().map(document => {
            if (document.resumeRequiredDocumentId == resumeRequiredDocumentId) {
              document.temporalFile = file;
              document.file = base64String;
              document.fileName = file.name;
            }
            return document;
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  }

  private addDocument(base64String: string, fileName: string, resumeRequiredDocumentId: number) {
    const resumeDocument: AddResumeDocument = {
      id: null,
      resumeId: this.resumeId(),
      resumeRequiredDocumentId: resumeRequiredDocumentId,
      fileId: null,
      file: base64String,
      fileName: fileName,
      isEnabled: true
    }
    this.loading.set({
      isLoading: true,
      id: resumeRequiredDocumentId
    });
    this.resumeService.addResumeDocument({
      ...resumeDocument
    }).subscribe({
      next: res => {
        this.loading.set({
          isLoading: false,
          id: resumeRequiredDocumentId
        });
        this.getDocuments();
      }, error: res => {
        this.loading.set({
          isLoading: false,
          id: resumeRequiredDocumentId
        });
        Swal.fire({
          title: 'Error',
          text: 'Ocurrió un error al guardar el documento.',
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
    })


  }

  deleteFile(item: Document) {
    const resumeId = this.resumeId();
    const id = item.resumeDocumentId;

    if (!resumeId || !id) {
      this.documents.set(this.documents().map(document => {
        if (document.resumeRequiredDocumentId == item.resumeRequiredDocumentId) {
          document.file = null;
          document.fileName = null;
        }
        return document;
      }));
      return;
    }

    Swal.fire({
      title: 'Eliminar',
      text: '¿Desea eliminar este documento?',
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: '#0d1174',
      cancelButtonColor: '#3a3a3a',
      confirmButtonText: 'Eliminar'
    }).then((resultmodal) => {
      if (resultmodal.isConfirmed) {
        this.resumeService.deleteResumeDocument(id).subscribe({
          next: res => {
           this.getDocuments();
          }, error: res => {
            Swal.fire({
              title: 'Error',
              text: 'Ocurrió un error al eliminar el documento.',
              icon: 'error',
              confirmButtonColor: '#d33'
            });
          }
        })
      }
    });
  }

  viewFile(item: Document) {
    if (item?.temporalFile && !item.resumeDocumentId) {
      const fileUrl = URL.createObjectURL(item.temporalFile);
      document.getElementById('docPrintDocumento')?.setAttribute('src', fileUrl);
    } else {
      const fileUrl = `${environment.api.url}/File/ViewFile/${item.fileId}`;
      document.getElementById('docPrintDocumento')?.setAttribute('src', fileUrl);
    }
  }

}